import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, LayoutTemplate } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useToast } from "@/components/ui/use-toast";
import type { Id } from "@/convex/_generated/dataModel";

import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

interface SaveTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  typstContent: string;
  documentTitle?: string;
  documentId?: Id<"documents">;
}

/**
 * Dialog for saving a document as a reusable template
 * Collects template name and description, then saves to userDocTemplates table
 */
export function SaveTemplateDialog({
  isOpen,
  onClose,
  typstContent,
  documentTitle,
  documentId,
}: SaveTemplateDialogProps) {
  const [name, setName] = useState(documentTitle ? `${documentTitle} Template` : "");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const { toast } = useToast();

  const createTemplate = useMutation(api.userDocTemplates.create);
  const subscriptionData = useQuery(api.subscriptions.getOrganizationSubscription);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter a name for your template.",
        variant: "destructive",
      });
      return;
    }

    if (!typstContent) {
      toast({
        title: "No content",
        description: "Document has no content to save as template.",
        variant: "destructive",
      });
      return;
    }

    // Free tier check
    const planType = subscriptionData?.organization?.planType;
    const isFree = !planType || planType === "basic";

    if (isFree) {
      setShowUpgradePrompt(true);
      return;
    }

    setIsSaving(true);
    try {
      await createTemplate({
        name: name.trim(),
        description: description.trim() || undefined,
        typstContent,
        sourceDocumentId: documentId,
      });

      toast({
        title: "Template saved",
        description: "Your document has been saved as a reusable template.",
      });

      // Reset form and close
      setName("");
      setDescription("");
      onClose();
    } catch (error: any) {
      // Also catch the backend error if frontend check was bypassed
      if (error.message && error.message.includes("TIER_RESTRICTION")) {
        setShowUpgradePrompt(true);
      } else {
        console.error("Failed to save template:", error);
        toast({
          title: "Failed to save template",
          description: error instanceof Error ? error.message : "An error occurred.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSaving) {
      setName(documentTitle ? `${documentTitle} Template` : "");
      setDescription("");
      onClose();
    }
  };

  return (
    <>
      <UpgradePromptDialog
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        title="Save Unlimited Templates"
        description="Saving custom templates is a Professional feature. Upgrade to save time and standardize your documents."
        feature="Document Templates"
      />

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="h-5 w-5 text-purple-600" />
              Save as Template
            </DialogTitle>
            <DialogDescription>
              Save this document as a reusable template. You can use it later to generate similar
              documents.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Professional Proposal Template"
                disabled={isSaving}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="template-description">Description (optional)</Label>
              <Textarea
                id="template-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe when to use this template..."
                rows={3}
                disabled={isSaving}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !name.trim()} className="gap-2">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <LayoutTemplate className="h-4 w-4" />
                  Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
